import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Field } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_EVENT = {
  name: "", description: "", date: "", start_time: "18:00", end_time: "22:00", location: "",
  latitude: "", longitude: "", radius_m: 150, registration_status: "OPEN", participant_quota: "", ticket_quota: "",
  event_type: "COMMUNITY", price: "",
  paid_waiver_title: "",
  paid_waiver_content: "",
};

export const toPayload = (v) => ({
  ...v,
  latitude: parseFloat(v.latitude),
  longitude: parseFloat(v.longitude),
  radius_m: parseInt(v.radius_m, 10),
  participant_quota: v.participant_quota === "" || v.participant_quota == null ? null : parseInt(v.participant_quota, 10),
  ticket_quota: v.ticket_quota === "" || v.ticket_quota == null ? null : parseInt(v.ticket_quota, 10),
  price: v.price === "" || v.price == null ? null : parseInt(v.price, 10),
});

export const fileToDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export function EventForm({ initial, onSubmit, submitLabel = "Simpan" }) {
  const [v, setV] = useState({ ...EMPTY_EVENT, ...initial });
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const useMyLocation = () => navigator.geolocation?.getCurrentPosition((p) => setV((x) => ({ ...x, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) })));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(toPayload(v));
    } finally {
      setBusy(false);
    }
  };
  const sel = (k, opts) => (
    <Select value={v[k]} onValueChange={(x) => setV({ ...v, [k]: x })}>
      <SelectTrigger data-testid={`event-${k}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([val, l]) => <SelectItem key={val} value={val}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2" data-testid="event-form">
      <div className="md:col-span-2"><Field label="Nama event"><Input value={v.name} onChange={on("name")} required data-testid="event-name-input" /></Field></div>
      <div className="md:col-span-2"><Field label={`Deskripsi (${(v.description || "").length}/200)`}><Textarea maxLength={200} value={v.description} onChange={on("description")} data-testid="event-description-input" /></Field></div>
      <Field label="Tanggal"><Input type="date" value={v.date} onChange={on("date")} required data-testid="event-date-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Mulai"><Input type="time" value={v.start_time} onChange={on("start_time")} required data-testid="event-start-input" /></Field>
        <Field label="Selesai"><Input type="time" value={v.end_time} onChange={on("end_time")} required data-testid="event-end-input" /></Field>
      </div>
      <div className="md:col-span-2"><Field label="Lokasi"><Input value={v.location} onChange={on("location")} required data-testid="event-location-input" /></Field></div>
      <Field label="Latitude"><Input value={v.latitude} onChange={on("latitude")} required data-testid="event-lat-input" /></Field>
      <Field label="Longitude"><Input value={v.longitude} onChange={on("longitude")} required data-testid="event-lng-input" /></Field>
      <Field label="Radius check-in (meter)"><Input type="number" value={v.radius_m} onChange={on("radius_m")} required data-testid="event-radius-input" /></Field>
      <div className="flex items-end"><Button type="button" variant="outline" onClick={useMyLocation} data-testid="event-use-location-button">Pakai lokasi saya</Button></div>
      <Field label="Kuota peserta unik" hint="Kosongkan = tanpa batas"><Input type="number" value={v.participant_quota ?? ""} onChange={on("participant_quota")} data-testid="event-participant-quota-input" /></Field>
      <Field label="Kuota tiket antrean" hint="Kosongkan = tanpa batas"><Input type="number" value={v.ticket_quota ?? ""} onChange={on("ticket_quota")} data-testid="event-ticket-quota-input" /></Field>
      <Field label="Status check-in">{sel("registration_status", [["OPEN", "Dibuka"], ["CLOSED", "Ditutup"]])}</Field>
      <Field label="Tipe event">{sel("event_type", [["COMMUNITY", "Komunitas / Kontribusi Kolektif"], ["PAID", "Berbayar"]])}</Field>
      {v.event_type === "PAID" && (
        <>
          <Field label="Harga event (Rp)" hint="Peserta mengajukan lalu menunggu persetujuan pembayaran admin.">
            <Input type="number" min="1000" value={v.price ?? ""} onChange={on("price")} required data-testid="event-price-input" />
          </Field>
          <Field label="Informasi rekening transfer" hint="Contoh: BCA 1234567890 a.n. Iya Lagi Manjat">
            <Textarea value={v.payment_transfer_info ?? ""} onChange={on("payment_transfer_info")} required data-testid="event-transfer-info-input" />
          </Field>
          <div className="md:col-span-2 border-t border-[#1A3323]/10 pt-4">
            <h3 className="font-extrabold text-[#1A3323]" data-testid="paid-event-waiver-heading">
              Waiver Khusus Event Berbayar
            </h3>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="paid-event-waiver-help">
              Peserta wajib menyetujui sebelum mendaftar. Perubahan teks menerbitkan versi baru.
            </p>
          </div>
          <Field label="Judul waiver khusus" hint="Contoh: Waiver Private Trip Tebing">
            <Input
              value={v.paid_waiver_title ?? ""}
              onChange={on("paid_waiver_title")}
              required
              data-testid="event-paid-waiver-title-input"
            />
          </Field>
          <Field label="Isi waiver khusus" hint="Minimal 20 karakter.">
            <Textarea
              rows={6}
              value={v.paid_waiver_content ?? ""}
              onChange={on("paid_waiver_content")}
              required
              data-testid="event-paid-waiver-content-input"
            />
          </Field>
        </>
      )}
      <div className="md:col-span-2">
        <Button type="submit" disabled={busy} className="h-11 rounded-full px-8" data-testid="event-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}</Button>
      </div>
    </form>
  );
}
